import React from 'react';
import {
  Animated,
  Easing,
  Pressable,
} from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';

interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string; // Compatibility
  accessibilityLabel?: string;
}

export function Switch({ checked, onCheckedChange, disabled = false, accessibilityLabel }: SwitchProps) {
  const theme = useTheme<Theme>();
  const animatedValue = React.useRef(new Animated.Value(checked ? 1 : 0)).current;

  React.useEffect(() => {
    Animated.timing(animatedValue, {
      toValue: checked ? 1 : 0,
      duration: 200,
      easing: Easing.ease,
      useNativeDriver: false,
    }).start();
  }, [checked, animatedValue]);

  const handlePress = () => {
    if (!disabled) {
      onCheckedChange(!checked);
    }
  };

  const trackColor = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [theme.colors.neutral200, theme.colors.primary],
  });

  const thumbPosition = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: [2, 22],
  });

  return (
    <Pressable
      onPress={handlePress}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ checked, disabled }}
      style={[styles.pressable, disabled && styles.pressableDisabled]}
    >
      <Animated.View style={[styles.track, { backgroundColor: trackColor }]}>
        <Animated.View style={[styles.thumb, { transform: [{ translateX: thumbPosition }] }]} />
      </Animated.View>
    </Pressable>
  );
}

const styles = StyleSheet.create((theme) => ({
  pressable: {
    opacity: 1,
  },
  pressableDisabled: {
    opacity: 0.5,
  },
  thumb: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadii.full,
    height: 20,
    position: 'absolute',
    top: 2,
    width: 20,
  },
  track: {
    borderColor: theme.colors.border,
    borderRadius: theme.borderRadii.full,
    borderWidth: 1,
    elevation: 0,
    height: 26,
    justifyContent: 'center',
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    width: 46,
  },
}));
